import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useAttendance } from "@/hooks/useAttendance";
import { employeeService } from "@/services/employeeService";
import { usersService } from "@/services/usersService";
import { Employee, Skill } from "@/types/employee";
import { WorkShift } from "@/types/attendance";
import {
  Loader2,
  Briefcase,
  User,
  Badge as BadgeIcon,
  Tag,
  ShieldCheck,
  Mail,
  Lock,
  UserPlus,
  XCircle,
  Copy,
  FileText,
  Clock,
  Check
} from "lucide-react";

import { rolesService } from "@/services/roles";
import { Role } from "@/types/roles";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface EmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  employee: Employee | null;
  slug: string;
  companyId: string;
  currentUserId: string;
}

type AccessMode = "none" | "create";

export function EmployeeModal({
  isOpen,
  onClose,
  onSave,
  employee,
  slug,
  companyId,
  currentUserId,
}: EmployeeModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const { shifts: availableShifts } = useAttendance(slug);
  const [activeTab, setActiveTab] = useState<"details" | "skills">("details");
  const [createdCredentials, setCreatedCredentials] = useState<{email: string, pass: string} | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    identification: "",
    position: "",
    area: "",
    work_shift_id: "none",
    accessMode: "none" as AccessMode,
    roleId: "none",
    email: "",
    password: "",
    selectedSkills: [] as string[],
  });

  const loadDependencies = useCallback(async () => {
    try {
      const [skillsRes, rolesRes] = await Promise.all([
        employeeService.getSkills(slug),
        rolesService.getRoles(slug)
      ]);
      
      const skillsData = skillsRes.data?.data || skillsRes.data || [];
      setAvailableSkills(Array.isArray(skillsData) ? skillsData : []);
      
      const rolesData = rolesRes.data || [];
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (error) {
      console.error("Error loading dependencies", error);
    }
  }, [slug]);

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    toast({
      title: "¡Copiado!",
      description: "Contraseña copiada al portapapeles.",
    });
    setTimeout(() => setIsCopied(false), 2000);
  };

  const downloadCredentials = (email: string, pass: string) => {
    const element = document.createElement("a");
    const file = new Blob([
      `CREDENCIALES DE ACCESO - PROGRAPHIC\n\n` +
      `Email: ${email}\n` +
      `Contraseña: ${pass}\n\n` +
      `URL de Acceso: ${window.location.origin}/login\n\n` +
      `Por seguridad, se recomienda cambiar la contraseña después del primer ingreso.`
    ], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = `credenciales_${email.split('@')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast({ title: "Descargado", description: "Archivo de credenciales generado correctamente" });
  };


  useEffect(() => {
    if (isOpen) {
      loadDependencies();
      if (employee) {
        setFormData({
          name: employee.name,
          identification: employee.identification,
          position: employee.position,
          area: employee.area,
          work_shift_id: employee.work_shift_id || "none",
          accessMode: "none",
          roleId: "none",
          email: "",
          password: "",
          selectedSkills: employee.skills?.map((s) => s.id) || [],
        });
        setCreatedCredentials(null);
      } else {
        setFormData({
          name: "",
          identification: "",
          position: "",
          area: "",
          work_shift_id: "none",
          accessMode: "none",
          roleId: "none",
          email: "",
          password: "",
          selectedSkills: [],
        });
        setCreatedCredentials(null);
      }
    }
  }, [isOpen, employee?.id, slug, loadDependencies]);

  const handleSkillToggle = (skillId: string) => {
    if (!skillId) return;
    setFormData((prev) => {
      const current = Array.isArray(prev.selectedSkills) ? prev.selectedSkills : [];
      const updated = current.includes(skillId)
        ? current.filter((id) => id !== skillId)
        : [...current, skillId];
      return { ...prev, selectedSkills: updated };
    });
  };

  const validateDetails = () => {
    if (!formData.name || !formData.identification || !formData.position) {
      toast({
        title: "Campos requeridos",
        description: "Por favor completa nombre, identificación y puesto.",
        variant: "destructive",
      });
      return false;
    }

    if (formData.accessMode === "create") {
      if (!formData.email || !formData.password || formData.roleId === "none") {
        toast({
          title: "Configuración de acceso incompleta",
          description: "Ingresa email, contraseña y selecciona un rol.",
          variant: "destructive",
        });
        return false;
      }
    }


    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (activeTab === "details") {
      if (validateDetails()) setActiveTab("skills");
      return;
    }

    if (!validateDetails()) return;

    try {
      setLoading(true);
      let targetUserId = null;

      // 1. Si el modo es 'create', primero creamos el usuario
      if (formData.accessMode === "create") {
        const [firstName, ...lastNameParts] = formData.name.split(" ");
        const newUserRes = await usersService.createUser({
          companySlug: slug,
          companyId: companyId,
          email: formData.email,
          password: formData.password,
          firstName: firstName || "Nuevo",
          lastName: lastNameParts.join(" ") || "Colaborador",
          roleId: formData.roleId,
          creatorUserId: currentUserId,
          is_active: true
        });
        targetUserId = newUserRes.data.id;
        setCreatedCredentials({ email: formData.email, pass: formData.password });
      }

      const payload: any = {
        slug,
        name: formData.name,
        identification: formData.identification,
        position: formData.position,
        area: formData.area,
        work_shift_id: formData.work_shift_id === "none" ? null : formData.work_shift_id,
        skills: formData.selectedSkills,
      };

      // Si creamos un nuevo acceso, usamos ese ID
      if (formData.accessMode === "create" && targetUserId) {
        payload.userId = targetUserId;
      } else if (employee?.user_id) {
        // Si ya tenía acceso, mantenemos el que tiene
        payload.userId = employee.user_id;
      }

      // 2. Guardar datos del empleado
      if (employee) {
        await employeeService.updateEmployee(employee.id, {
          ...payload,
          updaterUserId: currentUserId,
        });
      } else {
        await employeeService.createEmployee({
          ...payload,
          creatorUserId: currentUserId,
        });
      }


      toast({ 
        title: employee ? "Personal actualizado" : "Personal creado",
        description: "Se han guardado los cambios correctamente."
      });
      
      onSave();
      if (formData.accessMode !== "create") {
        onClose();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || error.message || "Ocurrió un error al guardar",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={loading ? undefined : onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden border-none shadow-2xl">
        <DialogHeader className="p-6 pb-0 border-b bg-white shrink-0">
          <div className="flex items-center justify-between mb-4">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              {employee ? "Editar Perfil de Personal" : "Alta de Nuevo Personal"}
            </DialogTitle>
          </div>

          {/* Navegación por Pestañas */}
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={`pb-3 text-sm font-bold transition-all relative px-1 ${
                activeTab === "details"
                  ? "text-blue-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-1 after:bg-blue-600 after:rounded-t-full"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              <div className="flex items-center gap-2">
                1. Datos y Acceso
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("skills")}
              className={`pb-3 text-sm font-bold transition-all relative px-1 ${
                activeTab === "skills"
                  ? "text-blue-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-1 after:bg-blue-600 after:rounded-t-full"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              <div className="flex items-center gap-2">
                2. Habilidades
                {formData.selectedSkills.length > 0 && (
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 h-4 px-1.5 text-[10px] ml-1">
                    {formData.selectedSkills.length}
                  </Badge>
                )}
              </div>
            </button>
          </div>
        </DialogHeader>

        {createdCredentials && (
          <div className="bg-blue-600 p-6 text-white animate-in fade-in zoom-in-95 duration-300 flex flex-col items-center text-center gap-4">
            <div className="bg-white/20 p-3 rounded-full">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">¡Acceso Creado con Éxito!</h2>
              <p className="text-blue-100 text-sm mt-1">Descarga las credenciales para entregarlas al nuevo colaborador.</p>
            </div>
            <div className="flex gap-3 mt-2">
              <Button 
                onClick={() => downloadCredentials(createdCredentials.email, createdCredentials.pass)}
                className="bg-white text-blue-600 hover:bg-blue-50 font-bold"
              >
                <FileText className="h-4 w-4 mr-2" />
                Descargar Credenciales (.txt)
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setCreatedCredentials(null);
                  onClose();
                }}
                className="border-white/30 text-white hover:bg-white/10 hover:text-white"
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 overflow-hidden">
          <ScrollArea className="flex-1 max-h-[65vh]">
            <div className="p-8 space-y-8">
              {activeTab === "details" && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  {/* SECCIÓN 1: DATOS PERSONALES */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        Información Básica
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-xs font-bold text-slate-600">
                          Nombre Completo *
                        </Label>
                        <div className="relative group">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                          <Input
                            id="name"
                            className="pl-9 h-11 border-slate-200 bg-slate-50/50 focus:bg-white rounded-xl transition-all"
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            placeholder="Ej: Juan Pérez"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="identification" className="text-xs font-bold text-slate-600">
                          ID / Identificación *
                        </Label>
                        <div className="relative group">
                          <BadgeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                          <Input
                            id="identification"
                            className="pl-9 h-11 border-slate-200 bg-slate-50/50 focus:bg-white rounded-xl transition-all"
                            value={formData.identification}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                identification: e.target.value,
                              })
                            }
                            placeholder="Ej: 12345678-9"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="position" className="text-xs font-bold text-slate-600">
                          Puesto o Cargo *
                        </Label>
                        <div className="relative group">
                          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                          <Input
                            id="position"
                            className="pl-9 h-11 border-slate-200 bg-slate-50/50 focus:bg-white rounded-xl transition-all"
                            value={formData.position}
                            onChange={(e) =>
                              setFormData({ ...formData, position: e.target.value })
                            }
                            placeholder="Ej: Vendedora Jr."
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="area" className="text-xs font-bold text-slate-600">
                          Área o Departamento
                        </Label>
                        <Input
                          id="area"
                          className="h-11 border-slate-200 bg-slate-50/50 focus:bg-white rounded-xl transition-all"
                          value={formData.area}
                          onChange={(e) =>
                            setFormData({ ...formData, area: e.target.value })
                          }
                          placeholder="Ej: Ventas"
                        />
                      </div>
                    </div>

                    {/* NUEVO: SECCIÓN DE TURNO */}
                    <div className="grid grid-cols-1 gap-6 pt-2">
                      <div className="space-y-2">
                        <Label htmlFor="work_shift" className="text-xs font-bold text-slate-600">
                          Turno de Trabajo Asignado
                        </Label>
                        <div className="relative group">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 z-10 transition-colors" />
                          <Select
                            value={formData.work_shift_id}
                            onValueChange={(val) => setFormData({ ...formData, work_shift_id: val })}
                          >
                            <SelectTrigger className="pl-9 h-11 border-slate-200 bg-slate-50/50 focus:bg-white rounded-xl transition-all">
                              <SelectValue placeholder="Seleccionar un turno corporativo..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" className="text-slate-400 italic">Sin turno asignado</SelectItem>
                              {availableShifts?.map((shift) => (
                                <SelectItem key={shift.id} value={shift.id}>
                                  {shift.name} ({shift.start_time.substring(0,5)} - {shift.end_time.substring(0,5)})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-[10px] text-slate-400 italic leading-relaxed">
                          La asignación de un turno es necesaria para el cálculo automático de horas extras.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* SECCIÓN 2: ACCESO AL SISTEMA */}
                  <div className="space-y-6 pt-2">
                    <div className="flex items-center gap-2 border-b border-blue-50 pb-2">
                      <h3 className="text-xs font-bold text-blue-500 uppercase tracking-widest">
                        Configuración de Acceso
                      </h3>
                    </div>

                    <TooltipProvider>
                      <div className="bg-slate-50/80 border border-slate-100 p-1.5 rounded-2xl flex gap-1 items-center shrink-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, accessMode: "none" })}
                              className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-xl transition-all ${
                                formData.accessMode === "none"
                                  ? "bg-white shadow-sm text-slate-800"
                                  : "text-slate-400 hover:text-slate-600"
                                }`}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                              Sin Acceso
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p>El empleado no podrá entrar al sistema</p></TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              disabled={!!employee?.user_id}
                              onClick={() => setFormData({ ...formData, accessMode: "create" })}
                              className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-xl transition-all ${
                                formData.accessMode === "create"
                                  ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                                  : !!employee?.user_id 
                                    ? "opacity-50 cursor-not-allowed text-slate-300" 
                                    : "text-slate-400 hover:text-slate-600"
                                }`}
                            >
                              <UserPlus className="h-3.5 w-3.5" />
                              Dar de Alta Acceso
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p>Autoriza acceso con un correo y contraseña para este empleado</p></TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>

                    <div className="animate-in fade-in zoom-in-95 duration-200">
                      {formData.accessMode === "create" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-4 bg-blue-50/30 rounded-2xl border border-blue-50">
                          <div className="space-y-2">
                            <Label className="text-[10px] font-bold text-blue-600 uppercase">Email de Acceso *</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-blue-400" />
                              <Input
                                placeholder="usuario@empresa.com"
                                className="h-10 pl-9 bg-white border-blue-100 rounded-lg text-sm"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[10px] font-bold text-blue-600 uppercase">Contraseña Temporal *</Label>
                            <div className="relative flex gap-2">
                              <div className="relative flex-1">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-blue-400" />
                                <Input
                                  type="text"
                                  placeholder="Contraseña"
                                  className="h-10 pl-9 bg-white border-blue-100 rounded-lg text-sm"
                                  value={formData.password}
                                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                              </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#";
                                    const pass = Array.from({ length: 10 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join("");
                                    setFormData(prev => ({ ...prev, password: pass }));
                                  }}
                                  className="h-10 px-3 border-blue-100 text-blue-600 hover:bg-blue-50"
                                  title="Generar contraseña"
                                >
                                  Generar
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={!formData.password}
                                  onClick={() => copyToClipboard(formData.password)}
                                  className={`h-10 px-3 border-blue-100 transition-all ${isCopied ? 'text-green-600 bg-green-50' : 'text-blue-600 hover:bg-blue-50'}`}
                                  title="Copiar contraseña"
                                >
                                  {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                </Button>
                              </div>
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <Label className="text-[10px] font-bold text-blue-600 uppercase">Rol Asignado *</Label>
                            <Select
                              value={formData.roleId}
                              onValueChange={(val) => setFormData({ ...formData, roleId: val })}
                            >
                              <SelectTrigger className="h-10 bg-white border-blue-100 rounded-lg text-sm">
                                <SelectValue placeholder="Seleccionar rol..." />
                              </SelectTrigger>
                              <SelectContent>
                                {roles.map((r) => (
                                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}


                      {formData.accessMode === "none" && (
                        <div className="p-6 border-2 border-dashed border-slate-100 rounded-2xl flex flex-col items-center justify-center text-center">
                          <div className="bg-slate-100 p-3 rounded-full mb-3">
                            <ShieldCheck className="h-5 w-5 text-slate-400" />
                          </div>
                          <p className="text-sm font-bold text-slate-600">Este colaborador no tiene acceso a Prographic</p>
                          <p className="text-[11px] text-slate-400 max-w-[280px] mt-1">
                            Podrás activarlo más tarde creando una cuenta de acceso.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "skills" && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-green-600 uppercase tracking-widest">
                      Competencias Técnicas
                    </h3>
                    <p className="text-sm text-slate-500">
                      Selecciona las habilidades que definen el perfil técnico del colaborador.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pb-8">
                    {Array.isArray(availableSkills) && availableSkills.length > 0 ? (
                      availableSkills.map((skill) => {
                        if (!skill || !skill.id) return null;
                        const isSelected = !!formData.selectedSkills?.includes(skill.id);
                        return (
                          <label
                            key={skill.id}
                            htmlFor={`skill-${skill.id}`}
                            className={`flex items-center space-x-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                              isSelected
                                ? "bg-blue-50/50 border-blue-500 shadow-sm"
                                : "bg-slate-50/50 border-slate-100 hover:border-slate-200"
                            }`}
                          >
                            <Checkbox
                              id={`skill-${skill.id}`}
                              checked={isSelected}
                              onCheckedChange={() => handleSkillToggle(skill.id)}
                              className={isSelected ? "data-[state=checked]:bg-blue-600 border-blue-600" : "border-slate-300"}
                            />
                            <span
                              className={`text-sm cursor-pointer flex-1 select-none font-medium ${
                                isSelected ? "text-blue-900 font-bold" : "text-slate-600"
                                }`}
                            >
                              {skill.name}
                            </span>
                          </label>
                        );
                      })
                    ) : (
                      <div className="col-span-2 text-center py-12 text-slate-400 border-2 border-dashed border-slate-100 rounded-3xl">
                        <Tag className="h-8 w-8 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">No hay habilidades disponibles para configurar.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="p-6 border-t bg-slate-50 shrink-0">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={onClose}
              className="font-bold text-slate-500 hover:bg-slate-200 h-11 px-6 rounded-xl"
            >
              Cancelar
            </Button>
            <div className="flex-1" />
            
            {activeTab === "details" ? (
              <Button
                key="btn-next"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (validateDetails()) setActiveTab("skills");
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-10 rounded-xl shadow-lg shadow-blue-200 transition-all hover:scale-[1.02]"
              >
                Siguiente
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setActiveTab("details")}
                  className="font-bold border-slate-200 h-11 px-6 rounded-xl"
                >
                  Regresar
                </Button>
                <Button
                  key="btn-save"
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-10 rounded-xl shadow-lg shadow-blue-200 transition-all hover:scale-[1.02]"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    employee ? "Guardar Cambios" : "Crear Perfil"
                  )}
                </Button>
              </div>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
